import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { CSSEditor, CSSEditorWidgetProperties } from 'components/Editors/components/Editor.style';

import {
    DatasourceModel,
    DatasourceType,
    JApplicationApi,
    JDatasourceApi,
    JFilebankApi,
    Network,
    WidgetDatasourceTableContent,
    WidgetEditorActions,
    WidgetEditorErrorActions,
} from 'jamespot-user-api';

import { JRCButton } from 'components/JRCButton/JRCButton';

import { StudioEditor } from './model/studio/StudioEditor';
import { DocEditor } from './model/doc/DocEditor';
import { EditorBaseContext, EditorContainerProvider, Handlers, HookIsActive } from '../context/provider';
import { JRCWidgetTableSharedEditor } from './JRCWidgetTableSharedEditor';

export type WidgetDatasourceHandlers = Handlers & {
    jamespot: {
        datasource: Omit<JDatasourceApi, 'jApi' | 'network'>;
        network: Pick<Network, 'token' | 'postFile'>;
        application: Pick<JApplicationApi, 'studioGet' | 'studioList'>;
        filebank: JFilebankApi;
    };
};

export type WidgetDatasourceContext = EditorBaseContext<WidgetDatasourceTableContent> & {
    handlers: WidgetDatasourceHandlers;
};

export const JRCWidgetDatasourceTableEditor = ({
    uri,
    limit = 20,
    tableHeadColor,
    tableHeadTextColor,
    tableHeadIconColor,
    tableRowColor,
    tableRowTextColor,
    tableColumnsData = [],
    tableBorderRadius,
    tableSizedColumns,
    tableSizedColumnsWidth,
    hook,
    handlers,
    onChange,
    onSave,
    onStateChange,
    onError,
}: WidgetDatasourceTableContent & {
    handlers: WidgetDatasourceHandlers;
} & {
    hook: {
        isActive: HookIsActive;
    };
} & WidgetEditorActions<WidgetDatasourceTableContent> &
    WidgetEditorErrorActions) => {
    const intl = useIntl();
    const [model, setModel] = useState<DatasourceModel | undefined>(undefined);
    const [type, setType] = useState<DatasourceType | undefined>(undefined);

    useEffect(() => {
        if (uri && !model) {
            handlers.handleStart();
            handlers.jamespot.datasource
                .getModel(uri)
                .then((response) => {
                    setModel(response.result.model);
                    setType(response.result.type);
                })
                .catch(handlers.handleCatch)
                .finally(handlers.handleFinally);
        }

        if (!uri) {
            setModel(undefined);
            setType(undefined);
        }
    }, [uri, onError, model, onStateChange, handlers]);

    if (uri && !model && !type) {
        return <></>;
    }

    return (
        <EditorContainerProvider<WidgetDatasourceContext>
            hook={hook}
            handlers={handlers}
            onSave={onSave}
            onChange={onChange}
            onStateChange={onStateChange}
            onError={onError}>
            <CSSEditor>
                <CSSEditorWidgetProperties>
                    {hook && hook.isActive('FileBankHook') && (
                        <DocEditor
                            model={model}
                            type={type}
                            uri={uri ?? undefined}
                            tableColumnsData={tableColumnsData}
                        />
                    )}
                    {hook && hook.isActive('StudioHook') && (
                        <StudioEditor
                            model={model}
                            type={type}
                            uri={uri ?? undefined}
                            tableColumnsData={tableColumnsData}
                        />
                    )}
                    {uri && tableColumnsData.length ? (
                        <JRCWidgetTableSharedEditor
                            limit={limit}
                            tableHeadColor={tableHeadColor}
                            tableHeadTextColor={tableHeadTextColor}
                            tableHeadIconColor={tableHeadIconColor}
                            tableRowColor={tableRowColor}
                            tableRowTextColor={tableRowTextColor}
                            tableSizedColumns={tableSizedColumns}
                            tableSizedColumnsWidth={tableSizedColumnsWidth}
                            tableBorderRadius={tableBorderRadius}
                            onChange={onChange}
                        />
                    ) : (
                        <></>
                    )}
                </CSSEditorWidgetProperties>
                <JRCButton onClick={() => onSave({})} color={'primary'} minWidth={'100%'} noMargin>
                    {intl.formatMessage({ id: 'PANEL_Save_And_Close' })}
                </JRCButton>
            </CSSEditor>
        </EditorContainerProvider>
    );
};
